import { createSelector } from 'reselect';
import FieldModes from '../constants/FieldModes';
import FieldDisplayTypes from '../constants/FieldDisplayTypes';
import TableEditabilityTypes from '../constants/TableEditabilityTypes';
import { swapId } from './utils';
import { isFieldHiddenInView } from './documentHelper';
import { applyLogic } from '@joyfill/conditional-logic';

const DECORATOR_ICON_WIDTH = 40;

export function getDecoratorColumnWidth(visibleLimitInRows) {
  if (visibleLimitInRows === 1) return DECORATOR_ICON_WIDTH;
  if (visibleLimitInRows === 2) return 2 * DECORATOR_ICON_WIDTH;
  return 2.5 * DECORATOR_ICON_WIDTH;
}

/**
 * Resolve the stored `editability` of a table field or collection schema into two flags.
 *
 * Only the exact tokens 'inline' and 'form' count, the same contract the iOS and Android
 * SDKs follow. Anything else ('INLINE', 'bogus', a non-array) is ignored. When no valid
 * token is left the default applies, which is both: an unrecognised value never invents
 * a restriction nobody set.
 *
 * @param {Array} editability
 * @returns {{ inline: Boolean, form: Boolean }}
 */
export const resolveEditability = (editability) => {
  const tokens = Array.isArray(editability) ? editability : [];

  const inline = tokens.includes(TableEditabilityTypes.inline);
  const form = tokens.includes(TableEditabilityTypes.form);

  if (!inline && !form) return { inline: true, form: true };

  return { inline, form };
};

/**
 * Where `editability` is enforced.
 *
 * Fill mode only: the builder canvas keeps working as it does today, and readonly/pdf already
 * lock every cell through `disabled`. The compact view (rte) is excluded outright. Its rows are
 * static merge-code text that is never editable inline, and its row form must keep opening
 * whatever editability says.
 *
 * @param {Object} params
 * @param {String} params.fieldMode
 * @param {String} params.displayType
 * @returns {Boolean}
 */
export const isEditabilityEnforced = ({ fieldMode, displayType }) => (
  fieldMode === FieldModes.fill && displayType !== FieldDisplayTypes.rte
);

/**
 * 
 * @param {Array} columns  // Array of column objects
 * @param {Object} fieldPositionColumns // Object containing column settings. iee { columnId: { hidden: true/false } }
 * @param {String} view
 * @param {String} mode - interaction mode (edit, fill, readonly, pdf)
 * @param {Array} docFields - doc.fields array for conditional logic evaluation
 * @param {String} indicatorMode - authoring mode. Falls back to `mode` when absent.
 *
 * Why two modes: a mobile view that mirrors desktop renders with `mode` forced to
 * readonly so the canvas stays non-interactive (see File.js modeOverride), but the
 * author is still editing. Hidden columns must survive the filter so the header can
 * badge them, which is what `indicatorMode` decides.
 * @returns Array
 */
export const filterHiddenTableColumns = (columns, fieldPositionColumns, view, mode, docFields, indicatorMode) => {
  const isAuthoring = (indicatorMode || mode) === FieldModes.edit;

  const evaluated = (!isAuthoring && docFields)
    ? applyLogic(columns, docFields, '_id')
    : columns;

  return evaluated.filter((column) => {
    if (!isAuthoring && column.hidden) return false;
    if (!isAuthoring && isFieldHiddenInView(column?.hiddenViews, view)) return false;
    return true;
  });
}

export const filterHiddenTableColumnsSelector = createSelector(
  (state) => state.columns,
  (state) => state.fieldPositionColumns,
  (state) => state.view,
  (state) => state.mode,
  (state) => state.docFields,
  (state) => state.indicatorMode,
  (columns, fieldPositionColumns, view, mode, docFields, indicatorMode) => {
    return filterHiddenTableColumns(columns, fieldPositionColumns, view, mode, docFields, indicatorMode);
  }
);

/**
 * Cleans a row object by:
 *  - Removing the "deleted" property
 *  - Removing empty values from the "cells" object
 *
 * @param {Object} row - The row object to clean
 * @returns {Object} - A new row object without "deleted" and cleaned "cells"
 */
export const cleanRow = (row) => {
  if (!row || typeof row !== 'object') return {};

  // Destructure and exclude "deleted"
  const { deleted, cells = {}, ...rest } = row;

  // Build a new cleaned cells object
  const cleanedCells = Object.entries(cells).reduce((acc, [key, value]) => {
    const isEmptyArray = Array.isArray(value) && value.length === 0;
    const isEmptyString = typeof value === 'string' && value.trim() === '';

    // Keep only non-empty values
    if (value !== null && value !== undefined && !isEmptyArray && !isEmptyString) {
      acc[key] = value;
    }

    return acc;
  }, {});

  // Return new object (immutability preserved)
  return {
    ...rest,
    cells: cleanedCells,
  };
};


/**
 * For Document fieldPositions that have multiple table cell fieldPositions associated
 * with the same table field, we need to keep the column property in sync.
 * 
 * @param {*} nextDoc 
 * @param {*} fileId 
 * @param {*} fieldId 
 * @param {*} columnChanges 
 */
export const syncTableFieldColumnIdChangeInAllViewFieldPositions = (nextDoc, fileId, fieldId, columnChanges) => {

  const { oldColumnId, newColumnId } = columnChanges;

  const fileIndex = nextDoc.files.findIndex(f => f._id === fileId);

  const updateFieldPositionColumnId = (fp) => {
    if (fp.field !== fieldId) return fp;
    const nextFp = { ...fp };

    /**
     * 1. Update the fieldPostion column property to the newColumnId if it matches the oldColumnId
     */
    if (nextFp.column === oldColumnId) nextFp.column = newColumnId;

    /**
     * 2. Update the tableColumns property to the newColumnId if it matches the oldColumnId
     */
    if (nextFp.tableColumns && nextFp.tableColumns[oldColumnId]) nextFp.tableColumns = swapId(nextFp.tableColumns, oldColumnId, newColumnId);

    return nextFp;

  }

  // Update primary view fieldPositions
  nextDoc.files[fileIndex].pages = nextDoc.files[fileIndex].pages.map(page => { 
    page.fieldPositions = page.fieldPositions.map(updateFieldPositionColumnId); 
    return page; 
  });

  // Update mobile view fieldPositions if they exist
  if (nextDoc.files[fileIndex].views && nextDoc.files[fileIndex].views.length > 0) {
    nextDoc.files[fileIndex].views = nextDoc.files[fileIndex].views.map(view => {
      if (view.pages) {
        view.pages = view.pages.map(page => {
          page.fieldPositions = page?.fieldPositions.map(updateFieldPositionColumnId);
          return page;
        });
      }
      return view;
    });
  }

  return nextDoc;

}

/**
 * Rewrite `condition.column` references inside a top-level (non-collection) table field's
 * columns when a column id is renamed. Conditional Logic (`logic`), Conditional Required Logic
 * (`requiredLogic`, `cellRequiredLogic`) and Cell Visibility Logic (`cellVisibilityLogic`) can all
 * reference a sibling column via `condition.column`, so renaming one column must update every
 * other column's conditions that point at the old id.
 *
 * Unlike the collection variant (`swapColumnIdInLogicConditions`), table conditions do not carry a
 * `schema` tag, so we match on `condition.column` alone.
 *
 * @param {Array} tableColumns
 * @param {String} oldColumnId
 * @param {String} newColumnId
 * @returns {Array} a new tableColumns array with rewritten condition.column references
 */
export const swapColumnIdInTableColumnsLogic = (tableColumns, oldColumnId, newColumnId) => {
  if (!Array.isArray(tableColumns) || !oldColumnId || oldColumnId === newColumnId) return tableColumns;

  const logicKeys = ['logic', 'requiredLogic', 'cellRequiredLogic', 'cellVisibilityLogic'];

  return tableColumns.map((column) => {
    if (!column || typeof column !== 'object') return column;

    let nextColumn = column;

    logicKeys.forEach((key) => {
      const logicObj = nextColumn[key];
      if (logicObj && Array.isArray(logicObj.conditions)) {
        const nextConditions = logicObj.conditions.map((condition) => (
          condition && condition.column === oldColumnId
            ? { ...condition, column: newColumnId }
            : condition
        ));
        nextColumn = { ...nextColumn, [key]: { ...logicObj, conditions: nextConditions } };
      }
    });

    return nextColumn;
  });
};